import { Text } from '../../../../components/base/Text';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { ComponentRegistry } from '../../../registry/ComponentRegistry';
import type {
	FieldConfiguration,
	ListBuilderConfiguration,
} from '../../../types';
import { useListBuilderContext } from '../../core/ListBuilderProvider';
import {
	BadgeDisplay,
	ChipDisplay,
	DateDisplay,
	DotDisplay,
	EmailDisplay,
	IconDisplay,
	ImageDisplay,
	NumberDisplay,
	PhoneDisplay,
	PillDisplay,
	RelativeDateDisplay,
	TextDisplay,
} from '../../display';

interface FieldPreviewProps {
	field: FieldConfiguration;
	previewValue?: string;
	configuration?: ListBuilderConfiguration;
}

export function FieldPreview({
	field,
	previewValue = 'Sample Text',
	configuration,
}: FieldPreviewProps) {
	const colors = useThemeColors();
	const { listKey } = useListBuilderContext();

	if (field.displayStyle === 'line_break') {
		return null;
	}

	const previewField = { ...field };

	// Create preview props
	const previewProps = {
		field: previewField,
		value: previewValue,
		data: { [previewValue]: previewValue, id: 1, _id: 1 }, // Add sample IDs for link preview
		listKey: configuration?.objects?.[0]?.objectTable || '',
		objects: configuration?.objects,
	};

	const renderPreviewContent = () => {
		switch (field.displayStyle) {
			case 'text':
				return <TextDisplay {...previewProps} />;
			case 'badge':
				return <BadgeDisplay {...previewProps} />;
			case 'pill':
				return <PillDisplay {...previewProps} />;
			case 'chip':
				return <ChipDisplay {...previewProps} />;
			case 'dot':
				return <DotDisplay {...previewProps} showLabel={true} />;
			case 'icon':
				return <IconDisplay {...previewProps} />;
			case 'phone':
				return <PhoneDisplay {...previewProps} value="+15551234567" />;
			case 'email':
				return <EmailDisplay {...previewProps} value="example@email.com" />;
			case 'date':
				return (
					<DateDisplay {...previewProps} value={new Date().toISOString()} />
				);
			case 'relative_date':
				return (
					<RelativeDateDisplay
						{...previewProps}
						value={new Date().toISOString()}
					/>
				);
			case 'number':
				return <NumberDisplay {...previewProps} value={1234.56} />;
			case 'currency':
				return <NumberDisplay {...previewProps} value={1234.56} />;
			case 'image':
				return (
					<ImageDisplay
						{...previewProps}
						value="https://via.placeholder.com/150"
					/>
				);
			case 'custom':
				if (field.customComponent?.id != null) {
					const customComponent = ComponentRegistry.getComponent(
						field.customComponent.id,
					);

					if (customComponent != null) {
						const CustomComponent = customComponent.component;

						return (
							<CustomComponent
								{...previewProps}
								{...field.customComponent.props}
							/>
						);
					}

					return (
						<div className="bg-red-100 border border-red-400 text-red-700 px-2 py-1 rounded text-xs">
							<strong>Missing:</strong> {field.customComponent.id}
						</div>
					);
				}

				return (
					<div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-2 py-1 rounded text-xs">
						<strong>No component ID</strong>
					</div>
				);
			default:
				return <TextDisplay {...previewProps} />;
		}
	};

	return (
		<div
			className={cn(
				'mt-4 p-3 rounded-lg text-center',
				colors.classes.bgSecondary,
			)}
		>
			<Text className="text-xs mb-2" color="secondary">
				Preview
			</Text>
			{renderPreviewContent()}
		</div>
	);
}
